import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  TextField,
} from "@material-ui/core";
import { FormikProps } from "formik";
import { useIntl } from "../../../../../_metronic/i18n/customUseIntl";
import { NOTIFICATION_USER_GROUPS } from "../../../models/Notification/notificationConstants";
import { notificationFormFieldsStyles } from "../../../../ThemeProvider";
import type { NotificationFormState } from "../../../models/Notification/notificationTypes";

type NotificationFormFieldsProps = {
  formik: FormikProps<NotificationFormState>;
  isSaving: boolean;
  isHelpCenter: boolean;
};

const NotificationFormFields = ({
  formik,
  isSaving,
  isHelpCenter,
}: NotificationFormFieldsProps) => {
  const intl = useIntl();
  const classes = notificationFormFieldsStyles();

  const allGroupsSelected =
    formik.values.groups.length === NOTIFICATION_USER_GROUPS.length;

  const handleGroupsChange = (val: string[]) => {
    if (val.includes("all")) {
      const next = allGroupsSelected ? [] : [...NOTIFICATION_USER_GROUPS];
      formik.setFieldValue("groups", next);
      return;
    }
    formik.setFieldValue("groups", val);
  };

  return (
    <>
      <TextField
        id="title"
        name="title"
        label={intl.formatMessage({ id: "NOTIFICATIONS.TITLE" })}
        placeholder={intl.formatMessage({ id: "NOTIFICATIONS.TITLE" })}
        fullWidth
        margin="normal"
        variant="outlined"
        value={formik.values.title}
        disabled={isSaving}
        onChange={formik.handleChange}
        error={formik.touched.title && Boolean(formik.errors.title)}
        helperText={formik.touched.title && formik.errors.title}
        className={classes.titleField}
      />

      <TextField
        id="publishAt"
        name="publishAt"
        label={intl.formatMessage({ id: "NOTIFICATIONS.PUBLISH_TIME" })}
        value={formik.values.publishAt}
        disabled={isSaving}
        type="datetime-local"
        onChange={formik.handleChange}
        error={formik.touched.publishAt && Boolean(formik.errors.publishAt)}
        helperText={formik.touched.publishAt && formik.errors.publishAt}
        margin="normal"
        variant="outlined"
        InputLabelProps={{ shrink: true }}
        fullWidth
      />

      <TextField
        id="expiresAt"
        name="expiresAt"
        label={intl.formatMessage({ id: "NOTIFICATIONS.EXPIRATION_TIME" })}
        value={formik.values.expiresAt}
        disabled={isSaving}
        type="datetime-local"
        onChange={formik.handleChange}
        error={formik.touched.expiresAt && Boolean(formik.errors.expiresAt)}
        helperText={formik.touched.expiresAt && formik.errors.expiresAt}
        margin="normal"
        variant="outlined"
        InputLabelProps={{ shrink: true }}
        fullWidth
      />

      <FormControl
        variant="outlined"
        fullWidth
        className={classes.groupsControl}
      >
        <InputLabel htmlFor="groups">
          {intl.formatMessage({ id: "USERS.GROUPS" })}
        </InputLabel>
        <Select
          multiple
          fullWidth
          input={
            <OutlinedInput
              label={intl.formatMessage({ id: "USERS.GROUPS" })}
              name="groups"
              id="groups"
              value={formik.values.groups}
              disabled={isSaving}
              onChange={(e) =>
                handleGroupsChange(e.target.value as unknown as string[])
              }
            />
          }
        >
          <MenuItem value="all" className={classes.selectAllMenuItem}>
            {allGroupsSelected
              ? intl.formatMessage({ id: "USERS.GROUPS.SELECT_NONE" })
              : intl.formatMessage({ id: "USERS.GROUPS.SELECT_ALL" })}
          </MenuItem>
          {NOTIFICATION_USER_GROUPS.map((group) => (
            <MenuItem key={group} value={group}>
              {group}
            </MenuItem>
          ))}
        </Select>
        {formik.touched.groups && Boolean(formik.errors.groups) && (
          <FormHelperText error>
            {formik.errors.groups as string}
          </FormHelperText>
        )}
      </FormControl>

      <FormControlLabel
        control={
          <Switch
            id="active"
            name="active"
            checked={formik.values.active}
            onChange={formik.handleChange}
            disabled={isSaving}
          />
        }
        label={intl.formatMessage({ id: "NOTIFICATIONS.ACTIVE" })}
      />

      {!isHelpCenter && (
        <FormControlLabel
          control={
            <Switch
              id="isWelcome"
              name="isWelcome"
              checked={formik.values.isWelcome}
              onChange={formik.handleChange}
              disabled={isSaving}
            />
          }
          label={`${intl.formatMessage({
            id: "NOTIFICATIONS.WELCOME_MESSAGE",
          })}?`}
        />
      )}
    </>
  );
};

export default NotificationFormFields;
